import React, { useState, useMemo } from 'react';
import ExcelImporter from '../../components/ExcelImporter';
import Select from 'react-select';
import './ScadenzeClienti.css';
import { format, isBefore, addDays, parseISO } from 'date-fns';
import { it } from 'date-fns/locale';

const ScadenzeClienti = () => {
  const [importedData, setImportedData] = useState([]);
  const [filtroCliente, setFiltroCliente] = useState(null);
  const [filtroSettore, setFiltroSettore] = useState(null);
  const [filtroStato, setFiltroStato] = useState(null);
  const [viewMode, setViewMode] = useState('table'); // 'table' o 'cards'

  // Stili personalizzati per react-select
  const customSelectStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: '40px !important',
      border: '1px solid #ddd !important',
      boxShadow: state.isFocused ? '0 0 0 1px #007bff !important' : 'none !important',
      '&:hover': {
        borderColor: '#007bff !important',
      },
      width: '100% !important',
      borderColor: state.isFocused ? '#007bff !important' : '#ddd !important',
      padding: '0 !important',
      margin: '0 !important',
      backgroundColor: '#fff !important',
      color: '#333 !important',
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 10000,
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 10000,
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#999 !important',
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#007bff !important' : state.isFocused ? '#f8f9fa !important' : '#fff !important',
      color: state.isSelected ? '#fff !important' : '#333 !important',
      '&:hover': {
        backgroundColor: '#f8f9fa !important',
        color: '#333 !important',
      },
    }),
  };

  // Funzione per determinare lo stato di scadenza
  const getStatoScadenza = (dataScadenza) => {
    if (!dataScadenza) return 'Non specificata';
    
    try {
      const data = typeof dataScadenza === 'string' ? parseISO(dataScadenza) : new Date(dataScadenza);
      const oggi = new Date();
      const traVenticinqueGiorni = addDays(oggi, 25);
      
      if (isBefore(data, oggi)) {
        return 'Scaduto';
      } else if (isBefore(data, traVenticinqueGiorni)) {
        return 'In scadenza';
      } else {
        return 'Futuro';
      }
    } catch (error) {
      return 'Data non valida';
    }
  };

  // Funzione per pulire e convertire i numeri
  const pulisciNumero = (valore) => {
    // Gestisce valori null, undefined, stringa vuota
    if (valore === null || valore === undefined || valore === '') {
      return 0;
    }
    
    // Se è già un numero valido, lo restituisce
    if (typeof valore === 'number' && !isNaN(valore)) {
      return valore;
    }
    
    // Converte in stringa e rimuove spazi
    let numeroStr = String(valore).trim();
    
    // Se la stringa è vuota dopo il trim, ritorna 0
    if (numeroStr === '') {
      return 0;
    }
    
    // Rimuove spazi, simboli di valuta e altri caratteri non numerici (eccetto punti, virgole e segni)
    numeroStr = numeroStr.replace(/[€$£¥\s]/g, '');
    
    // Gestisce il formato italiano (virgola come separatore decimale)
    // Se ci sono sia punti che virgole, assume che il punto sia il separatore delle migliaia
    if (numeroStr.includes(',') && numeroStr.includes('.')) {
      // Formato: 1.234,56 (italiano) -> rimuove punti e sostituisce virgola con punto
      numeroStr = numeroStr.replace(/\./g, '').replace(',', '.');
    } else if (numeroStr.includes(',')) {
      // Solo virgola presente
      const parti = numeroStr.split(',');
      if (parti.length === 2 && parti[1].length <= 2) {
        // Probabilmente decimali: 1234,56 -> 1234.56
        numeroStr = numeroStr.replace(',', '.');
      } else {
        // Probabilmente separatore migliaia: 1,234 -> 1234
        numeroStr = numeroStr.replace(/,/g, '');
      }
    }
    
    const numero = parseFloat(numeroStr);
    return isNaN(numero) ? 0 : numero;
  };

  // Elaborazione dei dati importati
  const datiElaborati = useMemo(() => {
    if (!importedData.length) {
      return [];
    }
    
    const result = importedData.map((item, index) => {
      const dataScadenza = item.dataScadenza || item.data_scadenza || item.scadenza || item.data;
      const cliente = item.cliente || item.ragioneSociale || item.ragione_sociale || item.nome || '';
      const settore = item.settore || item.categoria || item.tipo || 'Non specificato';
      // Prova tutte le possibili varianti del nome colonna per l'importo
      const importoRaw = item.importo || item.valore || item.amount || item.prezzo || item.totale || 
                         item.costo || item.euro || item.eur || item.price || item.value || 
                         item.Importo || item.IMPORTO || item.Valore || item.VALORE || 
                         item.Amount || item.AMOUNT || item.Prezzo || item.PREZZO || 
                         item.Totale || item.TOTALE || item.Costo || item.COSTO || '';
      
      const descrizione = item.descrizione || item.note || item.oggetto || '';
      
      const importoPulito = pulisciNumero(importoRaw);
      
      const processedItem = {
        id: index,
        cliente: cliente.toString(),
        settore: settore.toString(),
        dataScadenza,
        statoScadenza: getStatoScadenza(dataScadenza),
        importo: importoPulito,
        descrizione: descrizione.toString(),
        dataFormattata: dataScadenza ? format(new Date(dataScadenza), 'dd/MM/yyyy', { locale: it }) : 'N/A'
      };
      
      return processedItem;
    });
    
    return result;
  }, [importedData]);

  // Opzioni per i filtri
  const opzioniClienti = useMemo(() => {
    const clienti = [...new Set(datiElaborati.map(item => item.cliente))].filter(Boolean);
    return clienti.map(cliente => ({ value: cliente, label: cliente }));
  }, [datiElaborati]);

  const opzioniSettori = useMemo(() => {
    const settori = [...new Set(datiElaborati.map(item => item.settore))].filter(Boolean);
    return settori.map(settore => ({ value: settore, label: settore }));
  }, [datiElaborati]);

  const opzioniStati = [
    { value: 'Scaduto', label: 'Scaduto' },
    { value: 'In scadenza', label: 'In scadenza' },
    { value: 'Futuro', label: 'Futuro' },
    { value: 'Non specificata', label: 'Non specificata' },
    { value: 'Data non valida', label: 'Data non valida' }
  ];

  // Dati filtrati
  const datiFiltrati = useMemo(() => {
    let risultato = [...datiElaborati];
    
    if (filtroCliente) {
      risultato = risultato.filter(item => item.cliente === filtroCliente.value);
    }
    
    if (filtroSettore) {
      risultato = risultato.filter(item => item.settore === filtroSettore.value);
    }
    
    if (filtroStato) {
      risultato = risultato.filter(item => item.statoScadenza === filtroStato.value);
    }
    
    return risultato;
  }, [datiElaborati, filtroCliente, filtroSettore, filtroStato]);

  // Statistiche
  const statistiche = useMemo(() => {
    const totale = datiFiltrati.length;
    const scaduti = datiFiltrati.filter(item => item.statoScadenza === 'Scaduto').length;
    const inScadenza = datiFiltrati.filter(item => item.statoScadenza === 'In scadenza').length;
    const futuri = datiFiltrati.filter(item => item.statoScadenza === 'Futuro').length;
    
    const importoTotale = datiFiltrati.reduce((sum, item) => {
      return sum + item.importo;
    }, 0);
    
    return { totale, scaduti, inScadenza, futuri, importoTotale };
  }, [datiFiltrati]);

  const handleDataImported = (data) => {
    setImportedData(data);
  };

  const resetFiltri = () => {
    setFiltroCliente(null);
    setFiltroSettore(null);
    setFiltroStato(null);
  };

  const getClasseStato = (stato) => {
    switch (stato) {
      case 'Scaduto': return 'stato-scaduto';
      case 'In scadenza': return 'stato-in-scadenza';
      case 'Futuro': return 'stato-futuro';
      default: return 'stato-non-specificato';
    }
  };

  // Funzione per scaricare il template CSV
  const downloadTemplate = () => {
    const csvContent = [
      // Intestazioni con tutti i possibili nomi di campo
      'cliente,settore,dataScadenza,importo,descrizione',
      // Esempi di dati con diversi formati numerici
      'Azienda Esempio SRL,Tecnologia,2024-03-15,1500.00,Fattura servizi IT',
      'Cliente Test SpA,Consulenza,2024-02-28,"2.300,50",Consulenza strategica',
      'Fornitore ABC,Manifatturiero,2024-04-10,850.75,Fornitura materiali',
      'Ditta XYZ,Commercio,2024-05-20,"€ 1.250,00",Vendita prodotti'
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'template_scadenze_clienti.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="scadenze-clienti-container">
      <h2>Gestione Scadenze Clienti</h2>
      
      {/* Sezione importazione */}
      <div className="importazione-section">
        <div className="importazione-header">
          <h3>Importazione Dati</h3>
          <button className="btn-download-template" onClick={downloadTemplate}>
            📥 Scarica Template CSV
          </button>
        </div>
        <ExcelImporter onDataImported={handleDataImported} />
        <div className="template-info">
          <p><strong>Formato CSV richiesto:</strong></p>
          <p>Il file deve contenere le colonne: <code>cliente</code>, <code>settore</code>, <code>dataScadenza</code> (formato YYYY-MM-DD), <code>importo</code>, <code>descrizione</code></p>
          <p><strong>Formati numerici supportati:</strong> 1500.00, "2.300,50", "€ 1.250,00", 850.75 - Il sistema riconosce automaticamente virgole e punti come separatori decimali o delle migliaia.</p>
          <p>Sono accettati anche nomi alternativi come: ragioneSociale, ragione_sociale, nome per il cliente; categoria, tipo per il settore; data_scadenza, scadenza, data per la data; valore, amount per l'importo; note, oggetto per la descrizione.</p>
        </div>
      </div>

      {datiElaborati.length > 0 && (
        <>
          {/* Statistiche */}
          <div className="statistiche-container">
            <div className="statistica-card">
              <h3>Totale</h3>
              <span className="numero">{statistiche.totale}</span>
            </div>
            <div className="statistica-card scaduti">
              <h3>Scaduti</h3>
              <span className="numero">{statistiche.scaduti}</span>
            </div>
            <div className="statistica-card in-scadenza">
              <h3>In Scadenza</h3>
              <span className="numero">{statistiche.inScadenza}</span>
            </div>
            <div className="statistica-card futuri">
              <h3>Futuri</h3>
              <span className="numero">{statistiche.futuri}</span>
            </div>
            <div className="statistica-card importo">
              <h3>Importo Totale</h3>
              <span className="numero">€ {statistiche.importoTotale.toLocaleString('it-IT', { minimumFractionDigits: 2 })}</span>
            </div>
          </div>

          {/* Filtri */}
          <div className="filtri-container">
            <div className="filtro-gruppo">
              <label>Cliente:</label>
              <Select
                value={filtroCliente}
                onChange={setFiltroCliente}
                options={opzioniClienti}
                styles={customSelectStyles}
                className="react-select-container"
                classNamePrefix="react-select"
                menuPortalTarget={document.body}
                placeholder="Tutti i clienti"
                isClearable
              />
            </div>
            
            <div className="filtro-gruppo">
              <label>Settore:</label>
              <Select
                value={filtroSettore}
                onChange={setFiltroSettore}
                options={opzioniSettori}
                styles={customSelectStyles}
                className="react-select-container"
                classNamePrefix="react-select"
                menuPortalTarget={document.body}
                placeholder="Tutti i settori"
                isClearable
              />
            </div>
            
            <div className="filtro-gruppo">
              <label>Stato:</label>
              <Select
                value={filtroStato}
                onChange={setFiltroStato}
                options={opzioniStati}
                styles={customSelectStyles}
                className="react-select-container"
                classNamePrefix="react-select"
                menuPortalTarget={document.body}
                placeholder="Tutti gli stati"
                isClearable
              />
            </div>
            
            <div className="filtro-gruppo">
              <button className="btn-reset-filtri" onClick={resetFiltri}>
                Reset Filtri
              </button>
            </div>
          </div>

          {/* Toggle visualizzazione */}
          <div className="view-toggle">
            <button 
              className={`toggle-btn ${viewMode === 'table' ? 'active' : ''}`}
              onClick={() => setViewMode('table')}
            >
              Tabella
            </button>
            <button 
              className={`toggle-btn ${viewMode === 'cards' ? 'active' : ''}`}
              onClick={() => setViewMode('cards')}
            >
              Schede
            </button>
          </div>

          {/* Visualizzazione dati */}
          {viewMode === 'table' ? (
            <div className="tabella-container">
              <table className="scadenze-table">
                <thead>
                  <tr>
                    <th>Cliente</th>
                    <th>Settore</th>
                    <th>Data Scadenza</th>
                    <th>Stato</th>
                    <th>Importo</th>
                    <th>Descrizione</th>
                  </tr>
                </thead>
                <tbody>
                  {datiFiltrati.map((item) => (
                    <tr key={item.id}>
                      <td>{item.cliente}</td>
                      <td>{item.settore}</td>
                      <td>{item.dataFormattata}</td>
                      <td>
                        <span className={`stato-badge ${getClasseStato(item.statoScadenza)}`}>
                          {item.statoScadenza}
                        </span>
                      </td>
                      <td>€ {item.importo.toLocaleString('it-IT', { minimumFractionDigits: 2 })}</td>
                      <td>{item.descrizione}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="cards-container">
              {datiFiltrati.map((item) => (
                <div key={item.id} className="scadenza-card">
                  <div className="card-header">
                    <h3>{item.cliente}</h3>
                    <span className={`stato-badge ${getClasseStato(item.statoScadenza)}`}>
                      {item.statoScadenza}
                    </span>
                  </div>
                  <div className="card-body">
                    <p><strong>Settore:</strong> {item.settore}</p>
                    <p><strong>Data Scadenza:</strong> {item.dataFormattata}</p>
                    <p><strong>Importo:</strong> € {item.importo.toLocaleString('it-IT', { minimumFractionDigits: 2 })}</p>
                    {item.descrizione && <p><strong>Descrizione:</strong> {item.descrizione}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}

          {datiFiltrati.length === 0 && (
            <div className="no-data">
              <p>Nessun dato corrisponde ai filtri selezionati.</p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ScadenzeClienti;